import { FC, useCallback, useMemo, useState } from "react";
import { Trans } from "@lingui/macro";
import { BrandSaleType, WishlistItemType } from "src/types/graphql.d";
import { Flex } from "@otrium/core";
import { Tag } from "src/icons/Tag";
import { FollowBrandButton } from "src/molecules/FollowBrandButton";
import { BrandCover } from "src/molecules/BrandCover";
import { StyledText } from "src/modules/brands/components/BrandInfo.styled";
import { useAddToWishList } from "src/hooks/useAddToWishlist";
import { useDeleteWishlistItem } from "src/hooks/deleteWishlistItem";
import { useToggleWishlistBrandItem } from "src/hooks/toggleWishlistItem";
import { useTheme, Theme } from "@emotion/react";
import { useSegment } from "src/hooks/useSegment";
import { AlgoliaSearchBrand, Gender } from "src/types/Algolia";
import { NewBrandInfoModal } from "src/molecules/BrandInfoModal";

interface Props {
  brand: AlgoliaSearchBrand;
  genderId: Gender;
  isLoggedIn: boolean;
  isComingSoon?: boolean;
}

const BrandViewBrandCover: FC<Props> = ({
  brand,
  genderId,
  isLoggedIn,
  isComingSoon = false,
}) => {
  const [isOpenBrandInfo, setIsOpenBrandInfo] = useState(false);

  const { deleteWishListItem, loading: deleteLoading } = useDeleteWishlistItem({
    refetchQueries: ["userWishlistMetaData"],
  });

  const { addToWishlist, loading: addLoading } = useAddToWishList({
    refetchQueries: ["userWishlistMetaData"],
  });

  const { addedToWishlist } = useToggleWishlistBrandItem(brand.objectID);

  const theme: Theme = useTheme();

  const { brandFavourited, brandUnfavourited, segmentBrandInfoOpened } =
    useSegment();

  const brandWishlistTracking = useMemo(
    () => ({
      brandName: brand.name,
      brandId: brand.objectID,
    }),
    [brand.name, brand.objectID]
  );

  const onAddToWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await addToWishlist(itemType, itemId);
      void brandFavourited(brandWishlistTracking);
    },
    [addToWishlist, brandFavourited, brandWishlistTracking]
  );

  const onRemoveFromWishList = useCallback(
    async (itemType: WishlistItemType, itemId: string) => {
      await deleteWishListItem(itemType, itemId, {});
      void brandUnfavourited(brandWishlistTracking);
    },
    [deleteWishListItem, brandUnfavourited, brandWishlistTracking]
  );

  const onToggleFollow = useCallback(
    async (id: string, type: WishlistItemType) => {
      if (addedToWishlist) {
        await onRemoveFromWishList(type, id);
      } else {
        await onAddToWishList(type, id);
      }
    },
    [addedToWishlist, onAddToWishList, onRemoveFromWishList]
  );

  const handleCloseBrandInfo = useCallback(() => {
    setIsOpenBrandInfo(false);
  }, []);

  const onShowBrandInfo = useCallback(() => {
    void segmentBrandInfoOpened({
      pageType: "Brand Page",
      brandName: brand.name,
    });
    setIsOpenBrandInfo(true);
  }, [segmentBrandInfoOpened, brand.name]);

  const brandCoverActions =
    brand.brand_sale_type !== BrandSaleType.FlashSale ? (
      <>
        {isLoggedIn && brand.objectID && (
          <Flex
            sx={{ paddingLeft: "30px", border: "none" }}
            justifyContent="center"
            alignItems="center"
          >
            <FollowBrandButton
              following={addedToWishlist}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={() =>
                onToggleFollow(brand.objectID, WishlistItemType.Brand)
              }
              loading={deleteLoading || addLoading}
            />
          </Flex>
        )}
        <Flex
          onClick={onShowBrandInfo}
          as="button"
          sx={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            position: "relative",
            "& > svg": {
              path: {
                transition: "all 0.2s ease-in-out",
                stroke: `${theme.colors.tone.white}`,
                fill: "transparent",
              },
            },
            "&:hover > svg": {
              path: {
                fill: "rgba(255,255,255, 0.5)",
              },
            },
          }}
          ml={5}
          pr={3}
          justifyContent="center"
          alignItems="center"
        >
          <Tag />
          <StyledText ml={2}>
            <Trans>Info</Trans>
          </StyledText>
        </Flex>
      </>
    ) : null;

  return (
    <>
      <NewBrandInfoModal
        open={isOpenBrandInfo}
        onClose={handleCloseBrandInfo}
        brand={brand}
      />
      <BrandCover
        brand={brand}
        genderId={genderId}
        isComingSoon={isComingSoon}
        actions={brandCoverActions}
      />
    </>
  );
};

export default BrandViewBrandCover;
